import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import SearchField from "@components/ui/textfields/SearchField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { useState, useEffect } from "react";
import { SyncOperation } from "types";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

const statusLabels: Record<string, string> = {
  pending: "En attente",
  in_progress: "En cours",
  completed: "Terminé",
  failed: "Échoué",
};

const typeLabels: Record<string, string> = {
  create: "Création",
  update: "Modification",
  delete: "Suppression",
  upload: "Upload",
};

export const SyncOperations = () => {
  const syncOperations = useProjectStore((state) => state.syncOperations);
  const isLoading = useProjectStore((state) => state.isLoadingSyncOperations);
  const fetchSyncOperations = useProjectStore(
    (state) => state.fetchSyncOperations,
  );
  const clearCompletedSyncOperations = useProjectStore(
    (state) => state.clearCompletedSyncOperations,
  );
  const userData = useDataStore((state) => state.userData);
  const [value, setValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  useEffect(() => {
    if (userData?.org_id) fetchSyncOperations(userData.org_id);
  }, []);

  const filtered = syncOperations.filter((op) => {
    const matchesSearch =
      !value ||
      op.resource.toLowerCase().includes(value.toLowerCase()) ||
      op.type.toLowerCase().includes(value.toLowerCase()) ||
      op.resource_id.toLowerCase().includes(value.toLowerCase());
    const matchesStatus = !statusFilter || op.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const counts = {
    total: syncOperations.length,
    pending: syncOperations.filter((op) => op.status === "pending").length,
    in_progress: syncOperations.filter((op) => op.status === "in_progress")
      .length,
    completed: syncOperations.filter((op) => op.status === "completed").length,
    failed: syncOperations.filter((op) => op.status === "failed").length,
  };

  const formatDate = (op: SyncOperation) => {
    try {
      return op.created_at.toDate().toLocaleString("fr-FR");
    } catch {
      return "-";
    }
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 ml-10">
      <div className="flex flex-col w-400 gap-4">
        <div className="flex flex-col">
          <Text1>Opérations de synchronisation</Text1>
          <Text2>Debug: visualisez les opérations de sync mobile.</Text2>
          <Text3>
            Cette page affiche les opérations de synchronisation entre
            l'application mobile et le serveur.
          </Text3>
        </div>
      </div>

      {/* Stats */}
      <div className="flex flex-row gap-3 mt-4 max-w-900">
        {(
          [
            ["total", "Total", "bg-neutral-100 text-neutral-600"],
            ["pending", "En attente", statusColors.pending],
            ["in_progress", "En cours", statusColors.in_progress],
            ["completed", "Terminé", statusColors.completed],
            ["failed", "Échoué", statusColors.failed],
          ] as const
        ).map(([key, label, color]) => (
          <button
            key={key}
            className={`flex flex-col items-center px-4 py-2 rounded-lg border ${
              statusFilter === (key === "total" ? null : key) ||
              (key === "total" && !statusFilter)
                ? "border-neutral-400"
                : "border-transparent"
            } ${color}`}
            onClick={() =>
              setStatusFilter(key === "total" ? null : (key as string))
            }
          >
            <span className="text-h1 font-semibold">
              {counts[key as keyof typeof counts]}
            </span>
            <span className="text-h4">{label}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2 mt-4 bg-white max-w-900 p-4 rounded-lg border-1">
        <div className="flex flex-row gap-2 items-center">
          <div className="w-300">
            <SearchField
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="resource, type, id..."
            />
          </div>
          <SecondaryButton
            startIcon={<Icon1 name="sync" size={20} />}
            onClick={() =>
              userData?.org_id && fetchSyncOperations(userData.org_id)
            }
          >
            {isLoading ? "Chargement..." : "Rafraîchir"}
          </SecondaryButton>
          {counts.completed > 0 && (
            <SecondaryButton
              variant="small"
              danger={true}
              onClick={() =>
                userData?.org_id &&
                clearCompletedSyncOperations(userData.org_id)
              }
              label={`Supprimer terminées (${counts.completed})`}
            />
          )}
        </div>

        <div className="flex flex-col divide-y mt-2">
          {filtered.length === 0 && (
            <div className="text-h3 text-neutral-400 py-8 text-center">
              {isLoading
                ? "Chargement..."
                : "Aucune opération de synchronisation"}
            </div>
          )}
          {filtered.map((op) => (
            <div
              key={op.id}
              className="flex flex-row items-center px-4 py-2 gap-3"
            >
              <div
                className={`text-h4 px-2 py-0.5 rounded-full ${statusColors[op.status]}`}
              >
                {statusLabels[op.status]}
              </div>
              <div className="text-h3 font-medium bg-neutral-100 px-2 py-0.5 rounded">
                {typeLabels[op.type] || op.type}
              </div>
              <div className="text-h3 text-neutral-500 font-mono">
                {op.resource}
              </div>
              <div className="text-h4 text-neutral-400 font-mono truncate max-w-150">
                {op.resource_id}
              </div>
              {op.retry_count > 0 && (
                <div className="text-h4 text-orange-500">{op.retry_count}x</div>
              )}
              <div className="ml-auto text-h4 text-neutral-400">
                {formatDate(op)}
              </div>
              {op.error && (
                <div
                  className="text-h4 text-red-500 truncate max-w-200"
                  title={op.error}
                >
                  {op.error}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
